// NeuralNetwork.jsx
// ══════════════════════════════════════════════
// רשת נוירונים - שכבות מלמעלה למטה
// ══════════════════════════════════════════════

import { useEffect, useRef, useState } from "react";
import "../../../styles/navbar/CyberRoom/NeuralNetwork.css";

const API_BASE = "https://api.anti-bot.ai";

const LAYER_SIZES = [3, 5, 6, 5, 3];

function threatColor(level) {
  switch (level) {
    case "CRITICAL": return "#ff2244";
    case "HIGH":     return "#ff8c00";
    case "MEDIUM":   return "#ffd700";
    default:         return "#00ff64";
  }
}

function hexToRgb(hex) {
  const map = {
    "#44aaff": "68,170,255",
    "#77ccff": "119,204,255",
    "#00ff64": "0,255,100",
    "#ff2244": "255,34,68",
    "#ff8c00": "255,140,0",
    "#ffd700": "255,215,0",
  };
  if (map[hex]) return map[hex];
  const r = parseInt(hex.slice(1,3), 16);
  const g = parseInt(hex.slice(3,5), 16);
  const b = parseInt(hex.slice(5,7), 16);
  return `${r},${g},${b}`;
}

function seededRandom(seed) {
  const x = Math.sin(seed + 1) * 10000;
  return x - Math.floor(x);
}

export default function NeuralNetwork() {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);
  const [stats, setStats] = useState(null);
  const statsRef  = useRef(null);

  // ── שליפת נתונים ──
  useEffect(() => {
    async function fetchStats() {
      try {
        const res  = await fetch(`${API_BASE}/api/cyber/neural-stats`);
        const json = await res.json();
        if (json.success) {
          setStats(json);
          statsRef.current = json;
        }
      } catch (err) {
        console.error("NeuralNetwork fetch error:", err);
      }
    }
    fetchStats();
    const iv = setInterval(fetchStats, 30000);
    return () => clearInterval(iv);
  }, []);

  // ── אנימציה ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    // שכבות מלמעלה למטה
    function buildNeurons(w, h) {
      const neurons = [];
      const rows    = LAYER_SIZES.length;
      const yStep   = h / (rows + 1);
      LAYER_SIZES.forEach((count, row) => {
        const y     = yStep * (row + 1);
        const xStep = w / (count + 1);
        for (let n = 0; n < count; n++) {
          neurons.push({ x: xStep * (n + 1), y, row, idx: n });
        }
      });
      return neurons;
    }

    function draw(ts) {
    const w       = canvas.width;
    const h       = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const data    = statsRef.current;
    const color   = data ? threatColor(data.threat_level) : "#00ff64";
    const pulse   = (Math.sin(ts / 600) + 1) / 2;
    const neurons = buildNeurons(w, h);

    // ── קווים עם זרימה מונפשת ──
    for (let row = 0; row < LAYER_SIZES.length - 1; row++) {
        const from = neurons.filter(n => n.row === row);
        const to   = neurons.filter(n => n.row === row + 1);

        from.forEach((f, fi) => {
        to.forEach((t, ti) => {
            const seed   = row * 100 + fi * 10 + ti;
            const base   = seededRandom(seed);
            
            // גל שזורם מלמעלה למטה לפי זמן + שכבה
            const wave   = (Math.sin(ts / 400 - row * 1.2 + seed * 3) + 1) / 2;
            const active = base > 0.4;
            const alpha  = active ? 0.08 + wave * 0.5 : 0.03;

            ctx.beginPath();
            ctx.moveTo(f.x, f.y);
            ctx.lineTo(t.x, t.y);
            ctx.strokeStyle = active
            ? `rgba(${hexToRgb(color)},${alpha})`
            : `rgba(100,150,200,0.04)`;
            ctx.lineWidth = active ? 0.8 + wave * 0.8 : 0.3;
            ctx.stroke();
        });
        });
    }


    for (let row = 0; row < LAYER_SIZES.length - 1; row++) {
      const from = neurons.filter(n => n.row === row);
      const to   = neurons.filter(n => n.row === row + 1);

      from.forEach((f, fi) => {
        to.forEach((t, ti) => {
          const seed   = row * 100 + fi * 10 + ti;
          const active = seededRandom(seed) > 0.4;
          if (!active) return;

          const speed  = 0.0003 + seededRandom(seed * 2) * 0.0004;
          const offset = seededRandom(seed * 3);
          const t_pos  = ((ts * speed + offset) % 1);

          const px = f.x + (t.x - f.x) * t_pos;
          const py = f.y + (t.y - f.y) * t_pos;

          const grad = ctx.createRadialGradient(px, py, 0, px, py, 5);
          grad.addColorStop(0, `rgba(${hexToRgb(color)},0.9)`);
          grad.addColorStop(1, "transparent");
          ctx.beginPath();
          ctx.arc(px, py, 5, 0, Math.PI * 2);
          ctx.fillStyle = grad;
          ctx.fill();

          ctx.beginPath();
          ctx.arc(px, py, 2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${hexToRgb(color)},1)`;
          ctx.fill();
        });
      });
    }

    // ── נוירונים עם פולס ──
    neurons.forEach(({ x, y, row }) => {
        const isOutput = row === LAYER_SIZES.length - 1;
        const isInput  = row === 0;
        const baseR    = isInput || isOutput ? 7 : 5;
        
        // כל נוירון פועם בקצב שונה קצת
        const nPulse   = (Math.sin(ts / 500 + x * 0.05 + y * 0.03) + 1) / 2;
        const glow     = isOutput ? nPulse * 0.7 + 0.3 : nPulse * 0.4 + 0.2;
        const nColor   = isOutput ? color : isInput ? "#44aaff" : "#77ccff";

        // glow
        const grad = ctx.createRadialGradient(x, y, 0, x, y, baseR * 3);
        grad.addColorStop(0, `rgba(${hexToRgb(nColor)},${glow * 0.4})`);
        grad.addColorStop(1, "transparent");
        ctx.beginPath();
        ctx.arc(x, y, baseR * 3, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();

        // גוף
        ctx.beginPath();
        ctx.arc(x, y, baseR + nPulse * 1.5, 0, Math.PI * 2);
        ctx.fillStyle = nColor;
        ctx.globalAlpha = 0.6 + glow * 0.4;
        ctx.fill();
        ctx.globalAlpha = 1;

        // border
        ctx.beginPath();
        ctx.arc(x, y, baseR + nPulse * 1.5, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${hexToRgb(nColor)},0.9)`;
        ctx.lineWidth   = 1;
        ctx.stroke();
    });

    animRef.current = requestAnimationFrame(draw);
    }

    const ro = new ResizeObserver(() => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    });
    ro.observe(canvas);
    canvas.width  = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    animRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animRef.current);
      ro.disconnect();
    };
  }, []);

  const color = stats ? threatColor(stats.threat_level) : "#00ff64";

  return (
    <div className="nn-wrapper">

      <div className="nn-header">
        <span className="nn-title">LIR DEFENSE - רשת נירונים</span>
        <span className="nn-updated">
          {stats?.last_analyzed
            ? `עודכן: ${new Date(stats.last_analyzed).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
            : "—"}
        </span>
      </div>

      <div className="nn-canvas-wrap">
        <canvas ref={canvasRef} className="nn-canvas" />
        <div className="nn-labels-vertical">
          <span>INPUT</span>
          <span>PROCESSING</span>
          <span>OUTPUT</span>
        </div>
      </div>

      <div className="nn-meta">
        <span>ACCURACY: <strong style={{ color }}>{stats?.accuracy ?? "—"}%</strong></span>
      </div>

      <div className="nn-stats">
        <div className="nn-stat-box analyzed">
          <span className="nn-stat-value">{stats?.total_analyzed?.toLocaleString() ?? "—"}</span>
          <span className="nn-stat-label">ANALYZED</span>
        </div>
        <div className="nn-stat-box threats">
          <span className="nn-stat-value" style={{ color }}>{stats?.threats_detected ?? "—"}</span>
          <span className="nn-stat-label">THREATS</span>
        </div>
      </div>

    </div>
  );
}